import { MessageDeliveryStatus, MessageEventType } from './constants';
import type {
	IChoreographer,
	IChoreographerMessages,
	IChoreographerPlugin,
	IChoreographerPluginMap,
	IChoreographerProductMessageMap,
	IMessage,
	StartMessageDeliveryStatuses,
	StopMessageDeliveryStatuses,
} from './types';

// This timeout is used for any long-running messages that are either stuck for some reason, or that the user has yet to clear, to ensure that messaging doesn't stay blocked indefinitely
const CHOREOGRAPHER_MESSAGE_TTL = 300_000; // 5 minute TTL for messages

// Lazily instantiate this the first time it's requested
let singleton: Choreographer;

/**
 * Choreographer Core API. Used to ensure only a single message at a time can be started on screen for the current user experience.
 * Accepts any number of plugins that want to register for choreographer services, provided they adhere to the IChoreographerPlugin contract.
 */
export class Choreographer implements IChoreographer {
	protected _currentMessage: { productId: string; messageId: string } | null = null;
	protected _messages: IChoreographerProductMessageMap = new Map();
	protected _plugins: IChoreographerPluginMap = new Map();
	protected _messageTtl: ReturnType<typeof setTimeout> | null = null;
	protected _isDisabled: boolean = false;

	/**
	 * Static method to create and/or return a singleton of the choreographer API.
	 *
	 * @returns Shared instance of choreographer API.
	 */
	static getInstance(): Choreographer {
		if (!singleton) {
			singleton = new this();
		}

		return singleton;
	}

	private constructor() {}

	/**
	 * Sets a timeout to auto-expire any messaging that stays open for too long, to prevent messaging from being blocked indefinitely.
	 *
	 * @param productId Unique identifier for the product making the current startMessage request that triggered this message timeout.
	 * @param messageId Unique identifier for the messageId that triggered this message timeout.
	 */
	protected _setMessageTtl(productId: string, messageId: string) {
		this._currentMessage = { productId, messageId };
		this._messageTtl = setTimeout(() => {
			if (this._currentMessage) {
				void this.stopMessage(productId, messageId);
			}
		}, CHOREOGRAPHER_MESSAGE_TTL);
	}

	/**
	 * Checks to see if the requesting productId/messageId pair matches the data for the currently-displayed message.
	 *
	 * @param productId Unique identifier for the product being checked for a current message match.
	 * @param messageId Unique identifier for the messageId being checked for a current message match.
	 */
	protected _isCurrentMessage(productId: string, messageId: string) {
		return this._currentMessage?.productId === productId && this._currentMessage?.messageId === messageId;
	}

	/**
	 * Clears the timeout that was established for the currently-displayed message.
	 */
	protected _clearMessageTtlIfMatching() {
		if (this._messageTtl) {
			this._currentMessage = null;
			clearTimeout(this._messageTtl);
		}
	}

	/**
	 * Disables all plugins that are currently registered with the choreographer, called when a startMessage call is being dispatched.
	 *
	 * @param [exceptFor] An optional plugin instance to exclude from the items being disabled, e.g. the plugin responsible for the current startMessage call.
	 */
	protected _disablePlugins(exceptFor?: IChoreographerPlugin) {
		this._isDisabled = true;
		[...this._plugins.values()].forEach(p => {
			if (p !== exceptFor) {
				p.disable();
			}
		});
	}

	/**
	 * Enables all plugins that are currently registered with the choreographer, called when a matching stopMessage call is successful.
	 */
	protected _enablePlugins() {
		[...this._plugins.values()].forEach(p => p.enable());
		this._isDisabled = false;
	}

	/**
	 * A callback used to remove a plugin from the list of subscribers for the choreographer service.
	 *
	 * @param productId A unique string representing the plugin to be unregistered from the choreographer API.
	 */
	protected _unregisterPlugin(productId: string) {
		this._messages.delete(productId);
		this._plugins.delete(productId);
	}

	/**
	 * Determines whether any message is currently being displayed, by checking its own status and optionally, asking each registered plugin for its individual status.
	 *
	 * @returns A value reflecting whether a message is currently being displayed on screen.
	 */
	_isDisplayingMessage() {
		return (
			!!this._currentMessage ||
			[...this._plugins.values()].reduce((isDisplayingMessage, p) => {
				return isDisplayingMessage || p.isDisplayingMessage();
			}, false)
		);
	}

	/**
	 * This function is intended for test cleanup purposes only, *not* to be called by external callers.
	 *
	 * @ignore
	 * @param productId Unique identifier for the product whose messages are being requested
	 * @returns A Map of IMessage objects, keyed by unique messageId strings.
	 */
	_getMessages(productId: string): IChoreographerMessages {
		return this._messages.get(productId) ?? (new Map() as IChoreographerMessages);
	}

	/**
	 * Requests to start a message via the choreographer service, ensuring only a single message instance at a time on the screen.
	 * A successful request will block any other message attempts until such time as the matching stopMessage request is received,
	 * or a message TTL timeout has expired.
	 *
	 * @param productId Unique identifier for the product making the current startMessage request that triggered this message timeout.
	 * @param messageId Unique identifier for the messageId that triggered this message timeout.
	 * @param [plugin] An optional plugin instance to prevent from being disabled when this request starts.
	 * @returns A Promise that resolves to a MessageDeliveryStatus value, to denote the ultimate status of the attempted message start operation.
	 */
	async startMessage(
		productId: string,
		messageId: string,
		plugin?: IChoreographerPlugin,
	): Promise<StartMessageDeliveryStatuses> {
		if (this._isDisabled || this._isDisplayingMessage()) {
			// TODO: Implement this with *actual* logging
			// loggerInstance.blocked(messageId, this._currentMessage);
			return MessageDeliveryStatus.BLOCKED;
		}

		if (!this._hasMessageRecord(productId, messageId)) {
			return MessageDeliveryStatus.UNREGISTERED;
		}

		const message = this._ensureMessageRecord(productId, messageId);

		this._disablePlugins(plugin);

		const didStart = (await message.start()) ?? true;

		if (didStart) {
			this._setMessageTtl(productId, messageId);
			// TODO: Implement this with *actual* logging
			// loggerInstance.success(messageId);
			return MessageDeliveryStatus.STARTED;
		}

		this._enablePlugins();

		// TODO: Implement this with *actual* logging
		// loggerInstance.optOut(messageId, 'start');

		return MessageDeliveryStatus.OPTED_OUT;
	}

	/**
	 * Requests to stop a message via the choreographer service. A successful request will allow any other plugins to begin issuing startMessage requests again.
	 *
	 * @param productId Unique identifier for the product making the current stopMessage request.
	 * @param messageId Unique identifier for the messageId to be stopped.
	 * @returns A Promise that resolves to a MessageDeliveryStatus value, to denote the ultimate status of the attempted message stop operation.
	 */
	async stopMessage(productId: string, messageId: string): Promise<StopMessageDeliveryStatuses> {
		if (!this._hasMessageRecord(productId, messageId)) {
			return MessageDeliveryStatus.UNREGISTERED;
		}

		const message = this._ensureMessageRecord(productId, messageId);

		const didStop = (await message.stop()) ?? true;

		if (didStop) {
			// Only clear the message TTL and reenable plugins if we're stopping the *current* message
			if (this._isCurrentMessage(productId, messageId)) {
				this._clearMessageTtlIfMatching();
				this._enablePlugins();
			}
			return MessageDeliveryStatus.STOPPED;
		}

		// TODO: Implement this with *actual* logging
		// loggerInstance.optOut(messageId, 'stop');

		return MessageDeliveryStatus.OPTED_OUT;
	}

	/**
	 * Registers a plugin instance with the choreographer API. Allows the choreographer to enable and disable it as needed to ensure individual message experiences on screen.
	 *
	 * @param productId Unique identifier to register as the product owner of this plugin.
	 * @param plugin A plugin instance to be registered with the choreographer instance.
	 * @returns A callback to be used to unregister this plugin with the choreographer instance, for cleanup purposes, if necessary.
	 */
	registerPlugin<T extends IChoreographerPlugin>(productId: string, plugin: T): () => void {
		if (this._plugins.has(productId)) {
			throw new Error(`Choreographer plugin already registered for ${productId}!`);
		}

		this._plugins = this._plugins.set(productId, plugin);

		if (this._isDisabled) {
			plugin.disable();
		}

		return () => {
			this._unregisterPlugin(productId);
		};
	}

	/**
	 * Validates that a message record is present and ready to be retrieved and/or manipulated.
	 * Creates the message instance if it doesn't yet exist, initializing both start and stop callbacks to noop functions.
	 *
	 * @param productId Unique identifier to retrieve map of IMessage objects.
	 * @param messageId Unique identifier for the messageId to be retrieved.
	 * @returns IMessage object for the requested productId/messageId combination.
	 */
	_ensureMessageRecord(productId: string, messageId: string) {
		let productMessages = this._messages.get(productId);

		if (!productMessages) {
			productMessages = new Map();
			this._messages.set(productId, productMessages);
		}

		let message = productMessages.get(messageId);

		if (!message) {
			message = {
				[MessageEventType.START]: () => {},
				[MessageEventType.STOP]: () => {},
			};
			productMessages.set(messageId, message);
		}

		return message;
	}

	/**
	 * Checks for the existance of a message record for a given productId/messageId combination.
	 *
	 * @param productId Unique identifier to check for a map of IMessage objects.
	 * @param messageId Unique identifier to check for within the map of productId IMessage objects.
	 * @returns Boolean indicating whether or not an IMessage record exists for the requested productId/messageId combination.
	 */
	_hasMessageRecord(productId: string, messageId: string) {
		return !!this._messages.get(productId)?.has?.(messageId);
	}

	/**
	 * Subscribes callbacks to be used for starting and stopping message requests through the choreographer, keyed by a combination of productId and messageId.
	 *
	 * @param productId Unique identifier to be used as a primary key for this message subscription.
	 * @param messageId Unique identifier within the context of all productId message entries, to be used as a secondary key for this message subscription.
	 * @param options An object containing start and stop callback functions, to be invoked upon successful requests to startMessage and stopMessage, respectively.
	 */
	on(productId: string, messageId: string, options: Partial<IMessage>): void {
		const message = {
			...this._ensureMessageRecord(productId, messageId),
			...options,
		};
		this._messages.get(productId)!.set(messageId, message);
	}

	/**
	 * Subscribes a callback to be used for starting message requests through the choreographer, keyed by a combination of productId and messageId.
	 *
	 * @param productId Unique identifier to be used as a primary key for this message subscription.
	 * @param messageId Unique identifier within the context of all productId message entries, to be used as a secondary key for this message subscription.
	 * @param startCallback A callback to be invoked upon successful requests to startMessage.
	 */
	onStart(productId: string, messageId: string, startCallback: IMessage['start']): void {
		this.on(productId, messageId, { start: startCallback });
	}

	/**
	 * Subscribes a callback to be used for stopping message requests through the choreographer, keyed by a combination of productId and messageId.
	 *
	 * @param productId Unique identifier to be used as a primary key for this message subscription.
	 * @param messageId Unique identifier within the context of all productId message entries, to be used as a secondary key for this message subscription.
	 * @param stopCallback A callback to be invoked upon successful requests to stopMessage.
	 */
	onStop(productId: string, messageId: string, stopCallback: IMessage['stop']): void {
		this.on(productId, messageId, { stop: stopCallback });
	}

	/**
	 * Unsubscribes a message's start and stop message callbacks from the choreographer, keyed by a combination of productId and messageId.
	 *
	 * @param productId Unique identifier to be used as a primary key for unsubscribing this message.
	 * @param messageId Unique identifier within the context of all productId message entries, to be used as a secondary key for unsubscribing this message.
	 */
	off(productId: string, messageId: string): void {
		this._messages.get(productId)?.delete?.(messageId);
	}
}
